import {
  HexScheduleId,
  SavedViewId,
  ScheduleNotificationId,
  StaticCellId,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../store";

export interface ScheduleNotificationModalState {
  createCondition: boolean;
  isOpen: boolean;
  scheduleNotificationId: ScheduleNotificationId | null;
  staticCellId: StaticCellId | null;
  scheduleId: HexScheduleId | null;
  // If the notification modal is opened with a selected savedViewId
  savedViewId: SavedViewId | null;
  // Whether or not the step for saving or updating a saved view has completed.
  // This helps us track where in the notification flow we are.
  savedViewPrestepComplete: boolean;
}

export interface EditScheduleModalState {
  isOpen: boolean;
  scheduleId: HexScheduleId | null;
}

export type AppViewSliceState = {
  scheduleNotificationModal: ScheduleNotificationModalState;
  editScheduleModal: EditScheduleModalState;
  hasSchedules: boolean;
  editSavedViewDropdown: boolean;
  embedScheduleListModalIsOpen: boolean;
  /**
   * When this counter is incremented, trigger a rerun of a fresh app session
   * in AppRoute. This helps us refresh state to the default.
   */
  rerunAppSessionCounter: number;
};

const initialAppViewState: AppViewSliceState = {
  scheduleNotificationModal: {
    createCondition: false,
    isOpen: false,
    staticCellId: null,
    scheduleNotificationId: null,
    scheduleId: null,
    savedViewId: null,
    savedViewPrestepComplete: true,
  },
  editScheduleModal: {
    isOpen: false,
    scheduleId: null,
  },
  embedScheduleListModalIsOpen: false,
  hasSchedules: false,
  editSavedViewDropdown: false,
  rerunAppSessionCounter: 0,
};

const appViewSlice = createSlice({
  name: "appView",
  initialState: initialAppViewState,
  reducers: {
    rerunAppSession: (state) => {
      state.rerunAppSessionCounter += 1;
    },
    // This modal state is intended for more general use and doesn't assume
    // a starting scheduleId. If a staticCellId is provided, it will be used
    // to start a conditional notification (otherwise, it will start a subscription).
    openScheduleNotificationModalNewNotification: (
      state,
      action: PayloadAction<{
        createCondition: boolean;
        savedViewId: SavedViewId | null;
        staticCellId?: StaticCellId;
      }>,
    ) => {
      state.scheduleNotificationModal.isOpen = true;
      state.scheduleNotificationModal.scheduleId = null;
      state.scheduleNotificationModal.staticCellId =
        action.payload.staticCellId ?? null;
      state.scheduleNotificationModal.savedViewId = action.payload.savedViewId;
      state.scheduleNotificationModal.savedViewPrestepComplete = false;
      state.scheduleNotificationModal.createCondition =
        action.payload.createCondition && action.payload.staticCellId != null;
    },
    // This modal state requires a staticCellId and is intended for use in
    // cell-native UIs
    openScheduleNotificationModalForCell: (
      state,
      action: PayloadAction<{
        staticCellId: StaticCellId;
        savedViewId: SavedViewId | null;
        createCondition: boolean;
      }>,
    ) => {
      state.scheduleNotificationModal.isOpen = true;
      state.scheduleNotificationModal.staticCellId =
        action.payload.staticCellId;
      state.scheduleNotificationModal.savedViewId = action.payload.savedViewId;
      state.scheduleNotificationModal.savedViewPrestepComplete = false;
      state.scheduleNotificationModal.createCondition =
        action.payload.createCondition;
    },
    // This modal state requires a scheduleId and is intended for use in
    // schedule-native UIs
    openScheduleNotificationModalForSchedule: (
      state,
      action: PayloadAction<{
        scheduleId: HexScheduleId;
        createCondition: boolean;
        savedViewId: SavedViewId | null;
        staticCellId?: StaticCellId;
      }>,
    ) => {
      state.scheduleNotificationModal.isOpen = true;
      state.scheduleNotificationModal.scheduleId = action.payload.scheduleId;
      state.scheduleNotificationModal.savedViewId = action.payload.savedViewId;
      state.scheduleNotificationModal.savedViewPrestepComplete = false;
      state.scheduleNotificationModal.staticCellId =
        action.payload.staticCellId ?? null;
      state.scheduleNotificationModal.createCondition =
        action.payload.createCondition;
    },
    // This modal state assumes an existing notification coupled with an existing schedule
    openScheduleNotificationModalForNotification: (
      state,
      action: PayloadAction<{
        scheduleId: HexScheduleId;
        scheduleNotificationId: ScheduleNotificationId;
      }>,
    ) => {
      state.scheduleNotificationModal.isOpen = true;
      state.scheduleNotificationModal.scheduleId = action.payload.scheduleId;
      state.scheduleNotificationModal.scheduleNotificationId =
        action.payload.scheduleNotificationId;
      state.scheduleNotificationModal.savedViewPrestepComplete = true;
    },
    setScheduleNotificationSavedViewId: (
      state,
      action: PayloadAction<SavedViewId>,
    ) => {
      state.scheduleNotificationModal.savedViewId = action.payload;
      state.scheduleNotificationModal.savedViewPrestepComplete = true;
    },
    skipScheduleNotificationSavedViewPrestep: (state) => {
      state.scheduleNotificationModal.savedViewPrestepComplete = true;
    },
    closeScheduleNotificationModal(state) {
      state.scheduleNotificationModal.createCondition = false;
      state.scheduleNotificationModal.isOpen = false;
      state.scheduleNotificationModal.staticCellId = null;
      state.scheduleNotificationModal.scheduleId = null;
      state.scheduleNotificationModal.scheduleNotificationId = null;
      state.scheduleNotificationModal.savedViewId = null;
    },
    openEditScheduleModal: (state, action: PayloadAction<HexScheduleId>) => {
      state.editScheduleModal.isOpen = true;
      state.editScheduleModal.scheduleId = action.payload;
      state.scheduleNotificationModal.savedViewPrestepComplete = true;
    },
    closeEditScheduleModal: (state) => {
      state.editScheduleModal.isOpen = false;
      state.editScheduleModal.scheduleId = null;
    },
    setHasSchedules: (state, action: PayloadAction<boolean>) => {
      state.hasSchedules = action.payload;
    },
    openSavedViewDropdown(state) {
      state.editSavedViewDropdown = true;
    },
    closeSavedViewDropdown(state) {
      state.editSavedViewDropdown = false;
    },
    openEmbedScheduleListModal(state) {
      state.embedScheduleListModalIsOpen = true;
    },
    closeEmbedScheduleListModal(state) {
      state.embedScheduleListModalIsOpen = false;
    },
  },
});

export const selectRerunAppSessionCounter = (state: RootState): number =>
  state.appView.rerunAppSessionCounter;

export const selectNotificationModalState = (
  state: RootState,
): ScheduleNotificationModalState => state.appView.scheduleNotificationModal;

export const selectEditScheduleModalState = (
  state: RootState,
): EditScheduleModalState => state.appView.editScheduleModal;

export const selectHasSchedules = (state: RootState): boolean =>
  state.appView.hasSchedules;

export const selectEditSavedViewDropdown = (state: RootState): boolean =>
  state.appView.editSavedViewDropdown;

export const appViewReducer = appViewSlice.reducer;
export const {
  closeEditScheduleModal,
  closeEmbedScheduleListModal,
  closeSavedViewDropdown,
  closeScheduleNotificationModal,
  openEditScheduleModal,
  openEmbedScheduleListModal,
  openSavedViewDropdown,
  openScheduleNotificationModalForCell,
  openScheduleNotificationModalForNotification,
  openScheduleNotificationModalForSchedule,
  openScheduleNotificationModalNewNotification,
  rerunAppSession,
  setHasSchedules,
  setScheduleNotificationSavedViewId,
  skipScheduleNotificationSavedViewPrestep,
} = appViewSlice.actions;
